import React, { useMemo, useState } from 'react';
import { Archive, Search } from 'lucide-react';
import { Input } from '../ui/input';
import EmptyState from '../ui/empty-state';

const monthKey = (date) => (date || '').slice(0, 7);

const monthLabel = (key) => {
  const [year, month] = key.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

// Finished work: completed and cancelled jobs, newest first. Nothing is deleted,
// so every total, rating and report still counts these.
export const ArchivedJobs = ({ jobs, renderJob }) => {
  const [query, setQuery] = useState('');
  const [month, setMonth] = useState('all');

  const months = useMemo(() => (
    [...new Set((jobs || []).map((job) => monthKey(job.date)).filter(Boolean))].sort().reverse()
  ), [jobs]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (jobs || [])
      .filter((job) => (month === 'all' ? true : monthKey(job.date) === month))
      .filter((job) => {
        if (!needle) return true;
        const haystack = [job.job_number, job.client_name, job.job_type, job.location,
          job.client_email, (job.assigned_staff_emails || []).join(' ')]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(needle);
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [jobs, query, month]);

  return (
    <div className="space-y-4" data-testid="archived-jobs">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by order number, client, job type or venue"
            className="pl-9"
            data-testid="archived-search"
          />
        </div>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="sm:w-56"
          data-testid="archived-month"
        >
          <option value="all">All months</option>
          {months.map((key) => (
            <option key={key} value={key}>{monthLabel(key)}</option>
          ))}
        </select>
      </div>

      <p className="text-xs text-slate-500" data-testid="archived-count">
        {visible.length} archived job{visible.length === 1 ? '' : 's'}
        {month === 'all' ? '' : ` in ${monthLabel(month)}`}
      </p>

      {visible.length === 0 ? (
        <EmptyState
          icon={Archive}
          title="Nothing archived here"
          line="Jobs move here as soon as they are completed or cancelled."
          testId="archived-empty"
        />
      ) : (
        <div className="grid gap-4">
          {visible.map((job) => renderJob(job))}
        </div>
      )}
    </div>
  );
};

export default ArchivedJobs;
